import { computed, inject } from '@angular/core';

import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  type,
  withComputed,
  withMethods,
  withState
} from '@ngrx/signals';
import {
  entityConfig,
  removeEntity,
  setEntities,
  setEntity,
  withEntities
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, mergeMap, pipe } from 'rxjs';

import { StrictEntityMap } from 'src/app/shared/models/entity.models';
import {
  ErrorResponse,
  RxMethodCallback
} from 'src/app/shared/models/rxMethod.models';
import { SelectedEventDetailsStore } from 'src/app/shared/services/events/events.state';
import {
  CreateScheduleAdditionRequest,
  CreateScheduleAdditionResponse,
  GetScheduleAdditionsRequest,
  GetScheduleAdditionsResponse,
  ScheduleAddition,
  UpdateScheduleAdditionRequest,
  UpdateScheduleAdditionResponse
} from 'src/app/shared/services/events/schedule-additions/schedule-additions.models';
import { ScheduleAdditionService } from 'src/app/shared/services/events/schedule-additions/schedule-additions.service';
import { defaultScheduleAdditionsState } from 'src/app/shared/services/events/schedule-additions/schedule-additions.state.constants';

const eventScheduleAdditionsCollection = entityConfig({
  entity: type<ScheduleAddition>(),
  selectId: (scheduleAddition) => scheduleAddition.id,
  collection: 'eventScheduleAdditions'
});

export interface EventScheduleAdditionState {
  getScheduleAdditionsInflight: boolean;
  createScheduleAdditionInflight: boolean;
  updateScheduleAdditionInflight: boolean;
  deleteScheduleAdditionInflight: boolean;
}

export const EventScheduleAdditionsStore = signalStore(
  { providedIn: 'root' },
  withEntities(eventScheduleAdditionsCollection),
  withState<EventScheduleAdditionState>(defaultScheduleAdditionsState),
  withComputed((store) => ({
    strictScheduleAdditionsEntityMap: computed(
      () =>
        store.eventScheduleAdditionsEntityMap() as StrictEntityMap<ScheduleAddition>
    )
  })),
  withMethods((store) => {
    const service = inject(ScheduleAdditionService);
    const eventStore = inject(SelectedEventDetailsStore);
    return {
      getScheduleAdditions: rxMethod<
        GetScheduleAdditionsRequest &
          RxMethodCallback<GetScheduleAdditionsResponse>
      >(
        pipe(
          mergeMap(({ payload, page, size, onSuccess, onFail }) => {
            const eventId = eventStore.eventId();
            if (!eventId) {
              onFail?.();
              return EMPTY;
            }

            patchState<EventScheduleAdditionState>(store, {
              getScheduleAdditionsInflight: true
            });

            return service
              .getScheduleAdditions$(eventId, payload, page, size)
              .pipe(
                tapResponse({
                  next: (response) => {
                    patchState(
                      store,
                      setEntities(
                        response.content,
                        eventScheduleAdditionsCollection
                      )
                    );
                    patchState<EventScheduleAdditionState>(store, {
                      getScheduleAdditionsInflight: false
                    });
                    onSuccess?.(response);
                  },
                  error: (err?: ErrorResponse) => {
                    patchState<EventScheduleAdditionState>(store, {
                      getScheduleAdditionsInflight: false
                    });
                    onFail?.(err);
                  }
                })
              );
          })
        )
      ),
      createScheduleAddition: rxMethod<
        CreateScheduleAdditionRequest &
          RxMethodCallback<CreateScheduleAdditionResponse>
      >(
        pipe(
          mergeMap(({ payload, onSuccess, onFail }) => {
            const eventId = eventStore.eventId();
            if (!eventId) {
              onFail?.();
              return EMPTY;
            }

            patchState<EventScheduleAdditionState>(store, {
              createScheduleAdditionInflight: true
            });

            return service.createScheduleAddition$(eventId, payload).pipe(
              tapResponse({
                next: (response) => {
                  patchState(
                    store,
                    setEntity(response, eventScheduleAdditionsCollection)
                  );
                  patchState<EventScheduleAdditionState>(store, {
                    createScheduleAdditionInflight: false
                  });
                  onSuccess?.(response);
                },
                error: (err?: ErrorResponse) => {
                  patchState<EventScheduleAdditionState>(store, {
                    createScheduleAdditionInflight: false
                  });
                  onFail?.(err);
                }
              })
            );
          })
        )
      ),
      updateScheduleAddition: rxMethod<
        UpdateScheduleAdditionRequest &
          RxMethodCallback<UpdateScheduleAdditionResponse>
      >(
        pipe(
          mergeMap(({ payload, onSuccess, onFail }) => {
            const eventId = eventStore.eventId();
            if (!eventId) {
              onFail?.();
              return EMPTY;
            }

            patchState<EventScheduleAdditionState>(store, {
              updateScheduleAdditionInflight: true
            });

            return service.updateScheduleAddition$(eventId, payload).pipe(
              tapResponse({
                next: (response) => {
                  patchState(
                    store,
                    setEntity(response, eventScheduleAdditionsCollection)
                  );
                  patchState<EventScheduleAdditionState>(store, {
                    updateScheduleAdditionInflight: false
                  });
                  onSuccess?.(response);
                },
                error: (err?: ErrorResponse) => {
                  patchState<EventScheduleAdditionState>(store, {
                    updateScheduleAdditionInflight: false
                  });
                  onFail?.(err);
                }
              })
            );
          })
        )
      ),
      deleteScheduleAddition: rxMethod<
        { scheduleAdditionId: string } & RxMethodCallback<void>
      >(
        pipe(
          mergeMap(({ scheduleAdditionId, onSuccess, onFail }) => {
            const eventId = eventStore.eventId();
            if (!eventId) {
              onFail?.();
              return EMPTY;
            }

            patchState<EventScheduleAdditionState>(store, {
              deleteScheduleAdditionInflight: true
            });

            return service
              .deleteScheduleAddition$(eventId, scheduleAdditionId)
              .pipe(
                tapResponse({
                  next: () => {
                    patchState(
                      store,
                      removeEntity(
                        scheduleAdditionId,
                        eventScheduleAdditionsCollection
                      )
                    );
                    patchState<EventScheduleAdditionState>(store, {
                      deleteScheduleAdditionInflight: false
                    });
                    onSuccess?.();
                  },
                  error: (err?: ErrorResponse) => {
                    patchState<EventScheduleAdditionState>(store, {
                      deleteScheduleAdditionInflight: false
                    });
                    onFail?.(err);
                  }
                })
              );
          })
        )
      ),
      reset: () => patchState(store, defaultScheduleAdditionsState)
    };
  })
);
